// Context Builder Service for Plutus AI
// Builds request context with risk profile, session memory, trades, prices, behavior fingerprint
// SINGLE SOURCE OF TRUTH - No component may inject ad-hoc context

import { supabase } from '@/integrations/supabase/client';
import { RiskProfile } from '@/services/riskProfileService';
import { 
  getBehaviorFingerprint, 
  updateBehaviorFingerprint,
  formatFingerprintForContext,
  analyzeMessageBehavior,
  type BehaviorFingerprint 
} from '@/services/behaviorFingerprintService';

// ============================================
// CONTEXT STATUS TYPES - MANDATORY TRACKING
// ============================================

export type ContextStatus = 'loading' | 'ready' | 'missing' | 'error';

export interface ContextHealth {
  riskProfile: ContextStatus;
  marketPrices: ContextStatus;
  journalStats: ContextStatus;
  behaviorFingerprint: ContextStatus;
  overallReady: boolean;
  blockedReason: string | null;
}

export interface SessionMemory {
  lastAsset: string | null;
  lastConcern: string | null;
  typicalMistakes: string[];
  emotionalState: 'calm' | 'rushed' | 'frustrated' | 'overconfident';
  discussedTopics: string[];
  lastDecision: 'APPROVE' | 'WAIT' | 'NO-TRADE' | null;
  sessionStartTime: number;
}

export interface TodayStats {
  todayTradesCount: number;
  todayWinRate: number;
  todayPnL: number;
}

export interface JournalContext {
  totalDecisions: number;
  executedTrades: number;
  winRate: number;
  streak: string | null;
  tdiScore: number | null;
}

export interface PricesContext {
  gold?: { price: number; changePercent: number };
  crypto?: Record<string, { price: number }>;
  indices?: Record<string, { price: number }>;
}

// Create initial session memory
export const createSessionMemory = (): SessionMemory => ({
  lastAsset: null,
  lastConcern: null,
  typicalMistakes: [],
  emotionalState: 'calm',
  discussedTopics: [],
  lastDecision: null,
  sessionStartTime: Date.now()
});

// Detect emotional state from message patterns
export const inferEmotionalState = (message: string, previousState: SessionMemory['emotionalState']): SessionMemory['emotionalState'] => {
  const lower = message.toLowerCase();
  
  // Rushed indicators
  if (lower.includes('quick') || lower.includes('fast') || lower.includes('hurry') || 
      lower.includes('now') || lower.includes('asap') || lower.includes('missing out')) {
    return 'rushed';
  }
  
  // Frustration indicators
  if (lower.includes('damn') || lower.includes('shit') || lower.includes('frustrated') || 
      lower.includes('losing') || lower.includes('why') || lower.includes('hate') ||
      lower.includes('again') || lower.includes('another loss')) {
    return 'frustrated';
  }
  
  // Overconfidence indicators
  if (lower.includes('easy') || lower.includes('sure thing') || lower.includes("can't lose") ||
      lower.includes('guaranteed') || lower.includes('all in') || lower.includes('double down')) {
    return 'overconfident';
  }
  
  return previousState || 'calm';
};

// Extract typical mistake patterns
export const detectMistakePattern = (message: string): string[] => {
  const lower = message.toLowerCase();
  const patterns: string[] = [];
  
  if (!lower.includes('stop') && (lower.includes('entry') || lower.includes('buy') || lower.includes('sell'))) {
    patterns.push('missing_sl');
  }
  if (lower.includes('revenge') || lower.includes('get back') || lower.includes('recover')) {
    patterns.push('revenge_trading');
  }
  if (lower.includes('bigger') || lower.includes('more size') || lower.includes('double')) {
    patterns.push('over_sizing');
  }
  if (lower.includes('fomo') || lower.includes('missing') || lower.includes('chase')) {
    patterns.push('fomo');
  }
  
  return patterns;
};

// Update session memory based on user message
export const updateSessionMemory = (memory: SessionMemory, userMessage: string): SessionMemory => {
  const updated = { ...memory };
  
  // Detect emotional state
  updated.emotionalState = inferEmotionalState(userMessage, memory.emotionalState);
  
  // Detect mistake patterns
  const patterns = detectMistakePattern(userMessage);
  patterns.forEach(p => {
    if (!updated.typicalMistakes.includes(p)) {
      updated.typicalMistakes.push(p);
    }
  });
  
  // Track discussed assets
  const assetMatches = userMessage.match(/\b(gold|btc|bitcoin|eth|ethereum|sol|solana|us30|nas100|xrp|ada|doge|ger30|spx|spy)\b/gi);
  if (assetMatches && assetMatches.length > 0) {
    updated.lastAsset = assetMatches[0].toUpperCase();
  }
  
  // Track concerns mentioned
  const concernMatches = userMessage.match(/\b(stop.?loss|sl|risk|size|position|leverage|margin|drawdown)\b/gi);
  if (concernMatches && concernMatches.length > 0) {
    updated.lastConcern = concernMatches[0];
  }
  
  return updated;
};

// Fetch today's trading stats
export const fetchTodayStats = async (userId: string, riskAmount: number): Promise<TodayStats | null> => {
  if (!userId) return null;
  
  try {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('journal_entries')
      .select('outcome_status, outcome_r_multiple')
      .eq('user_id', userId)
      .gte('created_at', today);
    
    if (!data) return null;
    
    const trades = data.filter(e => ['WIN', 'LOSS', 'BREAKEVEN'].includes(e.outcome_status));
    const wins = trades.filter(e => e.outcome_status === 'WIN').length;
    const pnl = trades.reduce((sum, t) => sum + (t.outcome_r_multiple || 0), 0) * riskAmount;
    
    return {
      todayTradesCount: trades.length,
      todayWinRate: trades.length > 0 ? (wins / trades.length) * 100 : 0,
      todayPnL: pnl
    };
  } catch (e) {
    console.log('Could not fetch today stats:', e);
    return null;
  }
};

// Fetch all real-time prices for AI context
export const fetchAllPrices = async (): Promise<string> => {
  try {
    const [cryptoRes, goldRes, indicesRes] = await Promise.all([
      supabase.functions.invoke('get-crypto-prices', { body: { symbols: 'BTC,ETH,SOL,XRP,ADA,DOGE,AVAX,LINK,MATIC,DOT' } }),
      supabase.functions.invoke('get-gold-price'),
      supabase.functions.invoke('get-indices-prices')
    ]);
    
    let pricesContext = '\n\n📊 REAL-TIME MARKET PRICES:';
    
    // Gold
    if (goldRes.data?.price) {
      pricesContext += `\n- Gold (XAUUSD): $${goldRes.data.price.toLocaleString()} (${goldRes.data.changePercent > 0 ? '+' : ''}${goldRes.data.changePercent?.toFixed(2)}%)`;
    }
    
    // Crypto
    if (cryptoRes.data?.prices) {
      const crypto = cryptoRes.data.prices;
      pricesContext += `\n- Bitcoin (BTC): $${crypto.BTC?.price?.toLocaleString() || 'N/A'}`;
      pricesContext += `\n- Ethereum (ETH): $${crypto.ETH?.price?.toLocaleString() || 'N/A'}`;
      pricesContext += `\n- Solana (SOL): $${crypto.SOL?.price?.toLocaleString() || 'N/A'}`;
      pricesContext += `\n- XRP: $${crypto.XRP?.price?.toLocaleString() || 'N/A'}`;
    }
    
    // Indices
    if (indicesRes.data?.prices) {
      const indices = indicesRes.data.prices;
      pricesContext += `\n- US30 (Dow Jones): ${indices.US30?.price?.toLocaleString() || 'N/A'}`;
      pricesContext += `\n- NAS100 (Nasdaq): ${indices.NAS100?.price?.toLocaleString() || 'N/A'}`;
      pricesContext += `\n- SPP500 (S&P 500): ${indices.SPP500?.price?.toLocaleString() || 'N/A'}`;
    }
    
    return pricesContext;
  } catch (e) {
    console.log('Could not fetch all prices:', e);
    return '';
  }
};

// Fetch journal context
export const fetchJournalContext = async (userId: string): Promise<JournalContext | null> => {
  if (!userId) return null;
  
  try {
    const { data: stats } = await supabase.rpc('get_journal_stats', { 
      p_user_id: userId,
      p_days: 30 
    });
    
    if (stats) {
      return {
        totalDecisions: stats.total_decisions,
        executedTrades: stats.executed_trades,
        winRate: stats.win_rate,
        streak: stats.current_streak,
        tdiScore: stats.tdi_score
      };
    }
  } catch (e) {
    console.log('Journal context not available:', e);
  }
  
  return null;
};

// ============================================
// CONTEXT HEALTH CHECK - MANDATORY BEFORE AI CALLS
// ============================================

export interface ContextValidation {
  health: ContextHealth;
  isReady: boolean;
  blockMessage: string | null;
}

export const validateContextHealth = (
  riskProfile: RiskProfile | null,
  riskProfileLoading: boolean,
  pricesContext: string,
  pricesLoading: boolean
): ContextValidation => {
  const health: ContextHealth = {
    riskProfile: riskProfileLoading ? 'loading' : (riskProfile ? 'ready' : 'missing'),
    marketPrices: pricesLoading ? 'loading' : (pricesContext ? 'ready' : 'missing'),
    journalStats: 'ready', // Optional, doesn't block
    behaviorFingerprint: 'ready', // Optional, doesn't block
    overallReady: false,
    blockedReason: null
  };
  
  // Check blocking conditions
  if (health.riskProfile === 'loading') {
    health.blockedReason = 'Your risk profile is still loading. Please wait.';
  } else if (health.riskProfile === 'missing') {
    health.blockedReason = "You haven't configured a risk profile yet. I can't assess risk without it.";
  } else if (health.marketPrices === 'loading') {
    health.blockedReason = 'Market prices are syncing. One moment.';
  }
  
  health.overallReady = health.riskProfile === 'ready' && health.marketPrices !== 'loading';
  
  return {
    health,
    isReady: health.overallReady,
    blockMessage: health.blockedReason
  };
};

// ============================================
// STRUCTURED CONTEXT BLOCK - MANDATORY FORMAT
// ============================================

export interface StructuredContext {
  // CRITICAL: This field gates all risk-related responses
  risk_context_status: 'ready' | 'loading' | 'missing';
  riskProfile: {
    accountSize: number;
    riskPerTradePct: number;
    riskAmount: number;
    maxDailyLoss: number;
    allowedMarkets: string[];
    status: 'CONFIGURED' | 'MISSING';
  };
  marketSnapshot: {
    asset: string;
    price: number | 'MISSING';
    allPrices: string;
  };
  sessionState: {
    lastAsset: string | null;
    lastDecision: string | null;
    emotionalState: string;
  };
  journalStats: {
    totalDecisions: number;
    executedTrades: number;
    winRate: number | null;
    tdiScore: number | null;
  } | null;
  behaviorProfile: string | null;
}

export const buildStructuredContext = (
  selectedMarket: string,
  currentPrice: number | null,
  pricesContext: string,
  riskProfile: RiskProfile | null,
  riskAmount: number,
  maxDailyLoss: number,
  todayStats: TodayStats | null,
  journalContext: JournalContext | null,
  sessionMemory: SessionMemory,
  behaviorFingerprint?: BehaviorFingerprint | null,
  riskProfileLoading?: boolean
): StructuredContext => {
  // CRITICAL: Determine risk_context_status for AI gating
  const risk_context_status: 'ready' | 'loading' | 'missing' = 
    riskProfileLoading ? 'loading' : 
    (riskProfile && riskProfile.account_size) ? 'ready' : 'missing';
  
  return {
    risk_context_status,
    riskProfile: riskProfile ? {
      accountSize: riskProfile.account_size || 0,
      riskPerTradePct: riskProfile.risk_per_trade_pct || 0,
      riskAmount,
      maxDailyLoss,
      allowedMarkets: riskProfile.allowed_markets || [],
      status: 'CONFIGURED'
    } : {
      accountSize: 0,
      riskPerTradePct: 0,
      riskAmount: 0,
      maxDailyLoss: 0,
      allowedMarkets: [],
      status: 'MISSING'
    },
    marketSnapshot: {
      asset: selectedMarket,
      price: currentPrice ?? 'MISSING',
      allPrices: pricesContext || 'No prices loaded'
    },
    sessionState: {
      lastAsset: sessionMemory.lastAsset,
      lastDecision: sessionMemory.lastDecision,
      emotionalState: sessionMemory.emotionalState
    },
    journalStats: journalContext ? {
      totalDecisions: journalContext.totalDecisions,
      executedTrades: journalContext.executedTrades,
      winRate: journalContext.winRate,
      tdiScore: journalContext.tdiScore
    } : null,
    behaviorProfile: behaviorFingerprint ? formatFingerprintForContext(behaviorFingerprint) : null
  };
};

// Format structured context for AI injection + console logging
export const formatContextForAI = (ctx: StructuredContext): string => {
  const isConfigured = ctx.riskProfile.status === 'CONFIGURED';
  
  // CRITICAL: This status gates all risk-related responses in the AI
  const riskContextStatus = ctx.risk_context_status;

  const accountSizeLine = isConfigured
    ? `$${ctx.riskProfile.accountSize.toLocaleString()}`
    : 'N/A';
  const riskPerTradeLine = isConfigured
    ? `${ctx.riskProfile.riskPerTradePct}%`
    : 'N/A';
  const riskAmountLine = isConfigured
    ? `$${ctx.riskProfile.riskAmount.toLocaleString()}`
    : 'N/A';
  const maxDailyLossLine = isConfigured
    ? `$${ctx.riskProfile.maxDailyLoss.toLocaleString()}`
    : 'N/A';
  const allowedMarketsLine = isConfigured
    ? `[${ctx.riskProfile.allowedMarkets.join(', ')}]`
    : '[]';

  const priceLine = ctx.marketSnapshot.price === 'MISSING'
    ? 'MISSING'
    : `$${ctx.marketSnapshot.price.toLocaleString()}`;

  const block = `
=== PLUTUS CONTEXT ===
risk_context_status: ${riskContextStatus}

RISK_PROFILE:
- status: ${ctx.riskProfile.status}
- account_size: ${accountSizeLine}
- risk_per_trade_pct: ${riskPerTradeLine}
- risk_amount: ${riskAmountLine}
- max_daily_loss: ${maxDailyLossLine}
- allowed_markets: ${allowedMarketsLine}

MARKET_SNAPSHOT:
- asset: ${ctx.marketSnapshot.asset}
- price: ${priceLine}

SESSION_STATE:
- last_asset_discussed: ${ctx.sessionState.lastAsset || 'none'}
- last_decision: ${ctx.sessionState.lastDecision || 'none'}
- emotional_state: ${ctx.sessionState.emotionalState}
${ctx.journalStats ? `
JOURNAL_STATS:
- total_decisions: ${ctx.journalStats.totalDecisions}
- executed_trades: ${ctx.journalStats.executedTrades}
- win_rate: ${ctx.journalStats.winRate ?? 'N/A'}%
- tdi_score: ${ctx.journalStats.tdiScore ?? 'N/A'}` : ''}
${ctx.behaviorProfile ? `
BEHAVIOR_PROFILE:
${ctx.behaviorProfile}` : ''}
=== END CONTEXT ===

${ctx.marketSnapshot.allPrices}`;
  
  return block;
};

// Build full context message for AI (LEGACY - use buildStructuredContext + formatContextForAI instead)
export const buildContextMessage = (
  selectedMarket: string,
  currentPrice: number | null,
  pricesContext: string,
  riskProfileContext: string,
  riskAmount: number,
  maxDailyLoss: number,
  todayStats: TodayStats | null,
  journalContext: JournalContext | null,
  sessionMemory: SessionMemory,
  behaviorFingerprint?: BehaviorFingerprint | null
): string => {
  let contextMessage = `Current market: ${selectedMarket} @ $${currentPrice?.toLocaleString() || 'Chart context only'}`;
  contextMessage += pricesContext;
  
  // Add risk profile context - NO DEFAULTS
  if (riskProfileContext && riskProfileContext.includes('Account Size')) {
    contextMessage += `\n\n${riskProfileContext}`;
    contextMessage += `\n- Risk per trade: $${riskAmount.toLocaleString()}`;
    contextMessage += `\n- Max daily loss: $${maxDailyLoss.toLocaleString()}`;
  } else {
    contextMessage += `\n\n⚠️ RISK PROFILE: NOT CONFIGURED`;
  }
  
  if (todayStats) {
    contextMessage += `\n\nToday's Stats:
- Trades today: ${todayStats.todayTradesCount}
- Win rate today: ${todayStats.todayWinRate?.toFixed(1)}%
- PnL today: $${todayStats.todayPnL?.toFixed(2)}`;
  }
  
  if (journalContext) {
    contextMessage += `\n\nTrader Journal (30 days):
- Total decisions: ${journalContext.totalDecisions || 0}
- Executed trades: ${journalContext.executedTrades || 0}
- Win rate: ${journalContext.winRate || 'N/A'}%
- TDI Score: ${journalContext.tdiScore || 'N/A'}`;
  }
  
  // Add behavior fingerprint context (CRITICAL for tone adaptation)
  if (behaviorFingerprint) {
    contextMessage += `\n\n${formatFingerprintForContext(behaviorFingerprint)}`;
  }
  
  // Add session memory context
  if (sessionMemory.emotionalState !== 'calm') {
    contextMessage += `\n\n⚠️ Trader emotional state: ${sessionMemory.emotionalState}`;
  }
  
  return contextMessage;
};

// Fetch and update behavior fingerprint for a user
export const fetchBehaviorFingerprint = async (userId: string): Promise<BehaviorFingerprint | null> => {
  if (!userId) return null;
  return await getBehaviorFingerprint(userId);
};

// Update fingerprint (call this after interactions)
export const refreshBehaviorFingerprint = async (userId: string): Promise<BehaviorFingerprint | null> => {
  if (!userId) return null;
  return await updateBehaviorFingerprint(userId);
};

// Re-export for convenience
export { analyzeMessageBehavior, formatFingerprintForContext };
export type { BehaviorFingerprint };
